import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';

import css from './ListingCardErrorStrip.module.css';

/**
 * A failed action, stated on the card rather than only in a dialog.
 *
 * Sellers close dialogs immediately, so a failure that only lives in one is a failure
 * they never see. The case that matters most: marking an advertisement sold archives
 * it *and* cancels the subscription, and if only the first half succeeds the seller
 * believes they are finished while the direct debit keeps running. That was
 * previously indistinguishable from success — the error went to the store and was
 * read by nothing.
 *
 * The message always names both that money will be charged again and the date,
 * because "something went wrong" tells a seller nothing they can act on.
 *
 * @param {Object} props
 * @param {'subscriptionCancel'|'action'} props.kind
 * @param {string} [props.nextChargeDate] formatted; required for subscriptionCancel
 * @param {Function} [props.onRetry]
 */
const ListingCardErrorStrip = props => {
  const { kind, nextChargeDate, onRetry, className } = props;

  const isCancelFailure = kind === 'subscriptionCancel';

  return (
    <div className={classNames(css.root, className)} role="alert">
      <span className={css.title}>
        <FormattedMessage
          id={
            isCancelFailure
              ? 'ListingCardErrorStrip.cancelFailedTitle'
              : 'ListingCardErrorStrip.actionFailedTitle'
          }
        />
      </span>
      <span className={css.body}>
        {isCancelFailure ? (
          <FormattedMessage
            id="ListingCardErrorStrip.cancelFailedBody"
            values={{ date: nextChargeDate }}
          />
        ) : (
          <FormattedMessage id="ListingCardErrorStrip.actionFailedBody" />
        )}
      </span>
      {onRetry ? (
        <button
          type="button"
          className={css.retry}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onRetry();
          }}
        >
          <FormattedMessage id="ListingCardErrorStrip.retry" />
        </button>
      ) : null}
    </div>
  );
};

export default ListingCardErrorStrip;
